import { useCallback, useEffect, useState } from 'react'

import {
    PaymentDiscountCodeValidationLineRequest,
    usePaymentValidateDiscountCodeMutation,
} from '../../../../../common/redux/api/web-api'

type Text = (id: string, values?: Record<string, unknown>) => string

type DiscountCodeFeedback = {
    severity: 'success' | 'error'
    text: string
}

type DiscountCodeState = {
    input: string
    appliedCode: string | null
    feedback: DiscountCodeFeedback | null
    lastAttemptedCode: string | null
    submissionError: string | null
}

type DiscountCodeSubmission = { isValid: false } | { isValid: true; discountCode?: string }

const EMPTY_DISCOUNT_CODE_STATE: DiscountCodeState = {
    input: '',
    appliedCode: null,
    feedback: null,
    lastAttemptedCode: null,
    submissionError: null,
}

export const useInvoiceDiscountCode = ({
    enabled,
    discountCodeInfo,
    text,
}: {
    enabled: boolean
    discountCodeInfo?: PaymentDiscountCodeValidationLineRequest
    text: Text
}) => {
    const [state, setState] = useState<DiscountCodeState>(EMPTY_DISCOUNT_CODE_STATE)
    const [validateDiscountCode, { isLoading }] = usePaymentValidateDiscountCodeMutation()
    const normalizedInput = state.input.trim().toUpperCase()
    const isDisabled = discountCodeInfo?.quantity === 0

    useEffect(() => {
        setState(EMPTY_DISCOUNT_CODE_STATE)
    }, [discountCodeInfo?.productId, discountCodeInfo?.productPriceVersionId, enabled])

    const getSuccessfulFeedback = useCallback(
        (percentOff?: number | null, durationInMonths?: number | null) => {
            if (percentOff == null) return text('lblInvoiceDiscountCodeApplied')

            return (durationInMonths ?? 0) > 1
                ? text('lblInvoiceDiscountCodeAppliedWithPercentAndDuration', {
                      discountPercentage: percentOff,
                      durationInMonths,
                  })
                : text('lblInvoiceDiscountCodeAppliedWithPercent', { discountPercentage: percentOff })
        },
        [text]
    )

    const setInput = useCallback((input: string) => {
        setState({ ...EMPTY_DISCOUNT_CODE_STATE, input: input.toUpperCase() })
    }, [])

    const apply = useCallback(async () => {
        setState((current) => ({ ...current, input: normalizedInput, submissionError: null }))

        if (!normalizedInput || !discountCodeInfo?.productId || !discountCodeInfo.productPriceVersionId || isDisabled) {
            return
        }

        try {
            const response = await validateDiscountCode({
                scope: 'Organization',
                paymentDiscountCodeValidationRequest: {
                    code: normalizedInput,
                    scope: 'Organization',
                    lines: [discountCodeInfo],
                },
            }).unwrap()
            const resolvedCode = response.code?.trim() || normalizedInput
            const isValid = response.valid === true

            setState({
                input: resolvedCode,
                appliedCode: isValid ? resolvedCode : null,
                lastAttemptedCode: resolvedCode,
                submissionError: null,
                feedback: {
                    severity: isValid ? 'success' : 'error',
                    text: isValid
                        ? getSuccessfulFeedback(response.discount?.percentOff, response.discount?.durationInMonths)
                        : text('lblInvoiceDiscountCodeInvalid'),
                },
            })
        } catch {
            setState({
                input: normalizedInput,
                appliedCode: null,
                lastAttemptedCode: normalizedInput,
                submissionError: null,
                feedback: { severity: 'error', text: text('lblInvoiceDiscountCodeInvalid') },
            })
        }
    }, [discountCodeInfo, getSuccessfulFeedback, isDisabled, normalizedInput, text, validateDiscountCode])

    const validateForSubmission = useCallback((): DiscountCodeSubmission => {
        if (normalizedInput && normalizedInput !== (state.lastAttemptedCode ?? '')) {
            setState((current) => ({
                ...current,
                submissionError: text('lblInvoiceDiscountCodeApplyOrClear'),
            }))
            return { isValid: false }
        }

        return {
            isValid: true,
            discountCode: enabled && !isDisabled ? state.appliedCode ?? undefined : undefined,
        }
    }, [enabled, isDisabled, normalizedInput, state.appliedCode, state.lastAttemptedCode, text])

    return {
        input: state.input,
        feedback: state.feedback,
        submissionError: state.submissionError,
        isInputDisabled: isLoading || isDisabled,
        isApplyDisabled: !normalizedInput || isLoading || isDisabled,
        isApplying: isLoading,
        setInput,
        apply,
        validateForSubmission,
    }
}
